/**
 * 動画1回ぶんの「実際に再生した秒数」を数える。
 *
 * 分析画面の視聴時間は、これまで「動画を開いてからの経過時間」だったため、一時停止や
 * 見終わった画面の放置まで数えていた。ここでは再生位置が進んだぶんだけを足し上げる。
 * - 一時停止中は位置が進まないので数えない
 * - 早送り・巻き戻し（位置の飛び）は数えない
 *
 * 記録するのは実際に再生した秒数の合計と動画の長さだけで、「何秒地点まで進んだか」（再生位置）は記録しない。
 * 視聴者ごと・動画ごとの再生割合を新しく記録・表示する場合は、作る前に相談する（ADR-0023 C）。
 */
import { useEffect, useRef } from 'react'

/** 動画を離れるときに記録へ添える値 */
export type PlayedSecondsReading = {
  /** 実際に再生した秒数の合計 */
  playedSeconds: number
  /** 再生中の動画から読み取った長さ（秒）。読み取れなければ undefined */
  videoDuration?: number
}

/** 再生位置を見に行く間隔 */
const SAMPLE_INTERVAL_MS = 500
/**
 * 前回から進んだ位置が「経過時間 × 再生速度」をこれ以上超えたら、再生ではなく早送りとみなす。
 * 背景タブでは間隔が間引かれて経過時間が長くなるので、固定の上限ではなく経過時間を基準にする。
 */
const SEEK_TOLERANCE_SEC = 1

type Sample = { video: HTMLVideoElement; position: number; at: number }

/**
 * 再生秒数のカウンター。`getVideo` は数える対象の <video> を返す（作り直されても追従する）。
 */
export class PlayedSecondsCounter {
  private total = 0
  private last: Sample | null = null
  private duration: number | undefined

  constructor(private readonly getVideo: () => HTMLVideoElement | null) {}

  /** 今の再生位置を見て、前回から再生で進んだぶんを足す */
  sample(now: number = Date.now()): void {
    const video = this.getVideo()
    if (!video) {
      this.last = null
      return
    }
    if (Number.isFinite(video.duration) && video.duration > 0) {
      this.duration = video.duration
    }
    const position = video.currentTime
    const prev = this.last
    this.last = { video, position, at: now }
    // 別の <video> に替わった直後・一時停止中・位置を動かしている最中は数えない
    if (!prev || prev.video !== video || video.paused || video.seeking) return

    const advanced = position - prev.position
    const elapsedSec = (now - prev.at) / 1000
    const limit = elapsedSec * (video.playbackRate || 1) + SEEK_TOLERANCE_SEC
    if (advanced > 0 && advanced <= limit) {
      this.total += advanced
    }
  }

  /** 次の動画を数え始める前に、合計と前回の位置を捨てる */
  reset(): void {
    this.total = 0
    this.last = null
    this.duration = undefined
  }

  /** 動画を離れるときに呼ぶ。最後に位置を見てから合計を返し、次の動画のために捨てる */
  take(now: number = Date.now()): PlayedSecondsReading {
    this.sample(now)
    const reading: PlayedSecondsReading = {
      playedSeconds: Math.round(this.total * 1000) / 1000,
      videoDuration: this.duration,
    }
    this.reset()
    return reading
  }
}

/**
 * プレイヤーが開いている間だけ、一定間隔で再生位置を見に行くカウンターを返す。
 */
export function usePlayedSecondsCounter(getVideo: () => HTMLVideoElement | null, active: boolean): PlayedSecondsCounter {
  const getVideoRef = useRef(getVideo)
  getVideoRef.current = getVideo
  const counterRef = useRef<PlayedSecondsCounter | null>(null)
  if (!counterRef.current) {
    counterRef.current = new PlayedSecondsCounter(() => getVideoRef.current())
  }
  const counter = counterRef.current

  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => counter.sample(), SAMPLE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [active, counter])

  return counter
}

/**
 * ブラウザのタブごと閉じられたとき（pagehide）に一度だけ呼ぶ。
 * 通常の送信は非同期で、ページが消えると届かないため、呼び出し側で keepalive 付きで送る。
 */
export function usePageHide(onPageHide: () => void, active: boolean): void {
  const handlerRef = useRef(onPageHide)
  handlerRef.current = onPageHide

  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    const handle = () => handlerRef.current()
    window.addEventListener('pagehide', handle)
    return () => window.removeEventListener('pagehide', handle)
  }, [active])
}
